import React, { useCallback, useContext, useMemo } from 'react'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStickyFooter from '../../OfferPageStickyFooter'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import { getCheapestSelectedPackageOptionWithDefault, getFromDurationLabel, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import usePackageViewPrice from 'components/OfferPage/Villas/useVillasPrice'
import { getOptimizelyExperimentVariation, OptimizelyExperiments } from 'lib/optimizely/optimizelyUtils'
import { DAY_FORMAT, DAY_MONTH_NAME_SHORT, DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import useIsAvailableForPurchase from 'components/OfferPage/hooks/useIsAvailableForPurchase'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import FlashHotelAvailabilityButton from './FlashHotelAvailabilityButton'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'
import { pluralizeToString } from 'lib/string/pluralize'
import ModalContext from 'contexts/ModalContext'
import OfferCapacityWithAgesSelection from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import Modal from 'components/Luxkit/Modal/Modal'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'

interface Props {
  offer: App.Offer;
  rooms?: Array<App.RoomOccupants>;
  checkIn?: moment.Moment;
  duration: number;
  packageView: OfferPackageOptionView;
  durationLabel: string;
  isPerNightPricingTestEnabled?: boolean;
}

function HotelOfferPageStickyFooterVariant(props: Props) {
  const {
    rooms = EmptyArray,
    checkIn,
    duration,
    packageView,
    durationLabel,
    offer,
    isPerNightPricingTestEnabled,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const availableForPurchase = useIsAvailableForPurchase()

  const [price, value, discount] = usePackageViewPrice(packageView)

  const showPrices = packageView.capacityValid && packageView.hotelAvailable && availableForPurchase
  const guestsLabel = useMemo(() => `${formatOccupantsAsGuests(...rooms)} (${pluralizeToString(offer.saleUnit, rooms.length)})`, [offer.saleUnit, rooms])
  const dateText = useMemo(() => {
    if (checkIn) {
      const checkOut = checkIn.clone().add(duration, 'days')
      if (checkIn.isSame(checkOut, 'month')) {
        return `${checkIn.format(DAY_FORMAT)} - ${checkOut.format(DAY_MONTH_NAME_SHORT)}`
      } else if (checkIn.isSame(checkOut, 'year')) {
        return `${checkIn.format(DAY_MONTH_NAME_SHORT)} - ${checkOut.format(DAY_MONTH_NAME_SHORT)}`
      } else {
        return `${checkIn.format(DMY_CASUAL_SHORT_FORMAT)} - ${checkOut.format(DMY_CASUAL_SHORT_FORMAT)}`
      }
    }
  }, [checkIn, duration])

  const onOpenCalendar = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const canBook = !!(checkIn && rooms && availableForPurchase)
  const showModal = useContext(ModalContext)
  const onOpenCapcity = useCallback(() => {
    const capacities = offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
    showModal(<Modal
      title="Who is going?"
      primaryActionText="Apply"
      height="max"
      onPrimaryActionClick={(e, resolve) => {
        resolve()
        onOpenCalendar()
      }}
    >
      <OfferCapacityWithAgesSelection
        capacities={capacities}
        disableMultiRoom={isFlightForceBundled(offer)}
      />
    </Modal>)
  }, [offer, onOpenCalendar, showModal])

  return (
    <OfferPageStickyFooter visible callToActionDisabled >
      {showPrices && <Group direction="vertical" gap={4}>
        <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
          {rooms.length > 0 && <FilterChip size="small" onClick={onOpenCapcity}>{guestsLabel}</FilterChip>}
          {!!checkIn && <FilterChip size="small" onClick={onOpenCalendar}>{dateText}</FilterChip>}
        </Group>
        <div>
          {!canBook && <PriceRowCaption>{durationLabel}</PriceRowCaption>}
          <PriceRowPriceWithCaption
            size="M"
            price={price}
            caption={canBook ? 'From' : ''}
            saleUnit={isPerNightPricingTestEnabled ? 'night' : 'total'}
          />
          {!!value && <PriceRowValueDiscountWithCaption
            size="L"
            originalValue={value}
            discountPercentage={discount}
          />}
        </div>
      </Group>}
      <FlashHotelAvailabilityButton/>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
  durationLabel: getFromDurationLabel(state, appState),
  offer: state.offer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  isFlightSelected: state.flights,
  isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.pricePerNight),

}))(HotelOfferPageStickyFooterVariant)
